import { setup, assign, not } from 'xstate';
import { MachineStateType } from 'utils/xstate/types';
import { PrimaryLanguageEnum, RelationshipEnum } from 'domains/client';
import { TherapyTarget } from 'domains/signup/data/types';
import {
  AdultConcernOption,
  ClinicalArea,
  getClinicalAreas,
  PediatricConcernOption,
} from 'domains/signupv4/Mini-Screener/data/areaDetection';

export type TherapyConcerns = {
  concerns: string[];
  fullConcerns: PediatricConcernOption[] | AdultConcernOption[] | undefined;
  otherConcerns?: string;
  extraInformation?: string;
};

export type CompletedMiniScreenerMachineData = {
  clientFirstName?: string;
  clientLastName?: string;
  dob: Date;
  relationshipToClient: RelationshipEnum;
  concerns: TherapyConcerns;
  detectedAreas: ClinicalArea[];
  primaryLanguage: PrimaryLanguageEnum;
  previousEvaluation: boolean;
};

export type MiniScreenerResult = {
  data: CompletedMiniScreenerMachineData;
  earlyExit?: boolean;
};

// Machine's input
export type MiniScreenerMachineInput = {
  prevState?: Partial<CompletedMiniScreenerMachineData>;
  canExitByBack?: boolean;
  target: TherapyTarget;
};

type InternalContext = {
  data: Partial<CompletedMiniScreenerMachineData>;
  earlyExit?: boolean;
  input: Pick<MiniScreenerMachineInput, 'canExitByBack' | 'target'>;
};

export const miniScreenerMachine = setup({
  types: {
    context: {} as InternalContext,
    events: {} as
      | { type: 'back' }
      | { type: 'submitName'; firstName: string; lastName: string }
      | { type: 'submitDob'; dob: Date }
      | { type: 'submitRelationshipToClient'; relationshipToClient: RelationshipEnum }
      | ({
          type: 'submitConcerns';
        } & TherapyConcerns)
      | { type: 'submitPrimaryLanguage'; primaryLanguage: PrimaryLanguageEnum }
      | { type: 'submitPreviousEvaluation'; previousEvaluation: boolean },
    input: {} as MiniScreenerMachineInput,
    output: {} as MiniScreenerResult,
  },
  actions: {
    saveName: assign(({ context }, params: { firstName: string; lastName: string }) => ({
      data: { ...context.data, clientFirstName: params.firstName, clientLastName: params.lastName },
    })),
    saveDob: assign(({ context }, params: { dob: Date }) => ({
      data: { ...context.data, dob: params.dob },
    })),
    saveRelationshipToClient: assign(({ context }, params: { relationshipToClient: RelationshipEnum }) => ({
      data: { ...context.data, relationshipToClient: params.relationshipToClient },
    })),
    saveConcerns: assign(({ context }, params: TherapyConcerns) => ({
      data: {
        ...context.data,
        concerns: params,
      },
    })),
    savePrimaryLanguage: assign(({ context }, params: { primaryLanguage: PrimaryLanguageEnum }) => ({
      data: { ...context.data, primaryLanguage: params.primaryLanguage },
    })),
    savePreviousEvaluation: assign(({ context }, params: { previousEvaluation: boolean }) => ({
      data: { ...context.data, previousEvaluation: params.previousEvaluation },
    })),
  },
  guards: {
    canExitEarly: ({ context }) => context.input.canExitByBack === true,
    isChildTarget: ({ context }) => context.input.target === 'child',
    isSelfTarget: ({ context }) => context.input.target === 'adult',
    isAnotherAdultTarget: ({ context }) => context.input.target === 'another-adult',
  },
}).createMachine({
  /** @xstate-layout N4IgpgJg5mDOIC5QFsCWA7VBlAxgJzDHTDwFkBDHACwzADpYAXcvRgYgG0AGAXUVAAOAe1ipGqIen4gAHogCMAdi50AnOvUBmAGyKALHtXyATHoA0IAJ4K9AVjrHj22wA5Nr40dcuAvj4tomLgERCQU1LQMzKyc8nxIIMKi4pLScghKKhpaugZGphbWCKaqaraa8traXMYuXPqufgEY2PiExGSUNMRRLOwcxvGCImISUgnpmWrZOvqGJuZWiOoOTtrymnqeOvW2TSCBrSEd4d30RIwkAHLkyGBssACuAEZojDd33EOJIynjoOlbM46IpVLpVLZbKpNpDCogXPI6LYuCjjJoEfJKtpVL5-AcWsF2mEupELiQACJCZ4PF5vSnPL7SJKjVITeFbOguVRcWyOKFcSqbOEIFwuEHIrhbRzGZEuWyKfaHQmhToRHpkvAAYUkOBI6FgNNeYm16F1eH1jISzL+aQU8j0YuMXE0zr07kUJlswtF4pRUr5XDlCv26CEEDg0iVbRVp1oTN+Y1tCAAtNphanFQToycST0mH148lE2yEFthfJ6nQK-IoZ43IpalVM0Fs8S1ed0Jc8B8wIWWf9ZIg3YjjPIXNjPFxuR7FOXJSC1k7qmiIW5m0ciaqznQNfS+zaSw2suUUQ2XIpFC7NOXMWpFM5kdp3JonDp18qc+2d52SCazfr92LAFljlOg9C4dY9GxGoHRcRYikxRFVHvSEIOfV931bLdIgAMxaWAqEgQDWWAjJNAqMCII2B0TF2FwbxUBsqnte8G1MQM-D8IA */
  id: 'miniScreenerMachine',
  initial: 'start',
  context: ({ input }) => ({ data: { ...input.prevState }, input: input }),

  states: {
    start: {
      always: [
        {
          target: 'enterPreviousEvaluation', // If we have previous data, skip to the last state
          guard: ({ context }) => context.data.previousEvaluation !== undefined,
        },
        {
          target: 'enterDob',
          guard: {
            type: 'isSelfTarget',
          },
        },
        {
          target: 'enterClientName',
          guard: {
            type: 'isChildTarget',
          },
        },
        {
          target: 'enterClientName',
          guard: {
            type: 'isAnotherAdultTarget',
          },
        },
      ],
    },
    enterClientName: {
      on: {
        submitName: {
          target: 'enterDob',
          actions: {
            type: 'saveName',
            params: ({ event }) => event,
          },
        },
      },
    },
    enterDob: {
      on: {
        submitDob: [
          {
            target: 'enterRelationshipToClient',
            guard: not('isSelfTarget'),
            actions: {
              type: 'saveDob',
              params: ({ event }) => event,
            },
          },
          {
            target: 'enterConcerns',
            actions: {
              type: 'saveDob',
              params: ({ event }) => event,
            },
          },
        ],

        back: {
          target: 'enterClientName',
          guard: not('isSelfTarget'),
        },
      },
    },
    enterRelationshipToClient: {
      on: {
        submitRelationshipToClient: {
          target: 'enterConcerns',
          actions: {
            type: 'saveRelationshipToClient',
            params: ({ event }) => event,
          },
        },

        back: 'enterDob',
      },
    },
    enterConcerns: {
      on: {
        submitConcerns: {
          target: 'enterPrimaryLanguage',
          actions: {
            type: 'saveConcerns',
            params: ({ event }) => event,
          },
        },
        back: [
          {
            target: 'enterRelationshipToClient',
            guard: not('isSelfTarget'),
          },
          {
            target: 'enterDob',
          },
        ],
      },
    },
    enterPrimaryLanguage: {
      on: {
        submitPrimaryLanguage: {
          target: 'enterPreviousEvaluation',
          actions: {
            type: 'savePrimaryLanguage',
            params: ({ event }) => event,
          },
        },
        back: 'enterConcerns',
      },
    },
    enterPreviousEvaluation: {
      on: {
        submitPreviousEvaluation: {
          target: 'finished',
          actions: {
            type: 'savePreviousEvaluation',
            params: ({ event }) => event,
          },
        },
        back: 'enterPrimaryLanguage',
      },
    },

    finished: {
      type: 'final',
    },
  },

  output: ({ context }) => {
    const {
      concerns,
      dob,
      clientLastName,
      clientFirstName: clientName,
      relationshipToClient,
      primaryLanguage,
      previousEvaluation,
    } = context.data as CompletedMiniScreenerMachineData;
    const result: MiniScreenerResult = {
      data: {
        concerns,
        detectedAreas: getClinicalAreas(concerns.fullConcerns),
        dob,
        clientLastName,
        clientFirstName: clientName,
        relationshipToClient,
        primaryLanguage,
        previousEvaluation,
      },
      earlyExit: context.earlyExit,
    };

    return result;
  },
});

export type MiniScreenerEntryState = MachineStateType<typeof miniScreenerMachine>;
